import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Globe2, Heart, Eye, Share2, Trash2, Sparkles, Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/utils/i18n";

/**
 * Hall of Stories — opt-in publish panel rendered inside SettingsPanel.
 * Lets the player publish their saga to the public Hall, edit the title /
 * description (re-snapshotting stats), or remove it. The whole flow is
 * client-driven; the backend lives in routes/hall.py.
 */
export default function PublishStoryPanel({ gameId, lang = "it" }) {
  const { token, API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [story, setStory] = useState(null); // null = not published
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const isIt = lang === "it";

  const STR = {
    section: isIt ? "Hall of Stories" : "Hall of Stories",
    intro: isIt
      ? "Condividi pubblicamente la tua saga. Tutti potranno sfogliare titolo, capitoli e boss sconfitti. Niente pagine narrative — solo una vetrina."
      : "Share your saga publicly. Everyone can browse the title, chapters and bosses you defeated. No story pages exposed — only a showcase.",
    titleLabel: isIt ? "Titolo della saga" : "Saga title",
    titlePlaceholder: isIt ? "Es: La caduta di Eldoria" : "e.g. The Fall of Eldoria",
    descLabel: isIt ? "Descrizione (max 500 caratteri)" : "Description (max 500 chars)",
    descPlaceholder: isIt
      ? "Una breve descrizione della tua avventura per attirare lettori..."
      : "A short description of your adventure to attract readers...",
    publishBtn: isIt ? "Pubblica nella Hall" : "Publish to Hall",
    updateBtn: isIt ? "Aggiorna pubblicazione" : "Update publication",
    unpublishBtn: isIt ? "Rimuovi dalla Hall" : "Remove from Hall",
    publishedBadge: isIt ? "Pubblicato" : "Published",
    notPublished: isIt ? "Non pubblicato" : "Not published",
    likes: isIt ? "Mi piace" : "Likes",
    views: isIt ? "Visite" : "Views",
    chapters: isIt ? "Capitoli" : "Chapters",
    bosses: isIt ? "Boss sconfitti" : "Bosses defeated",
    pages: isIt ? "Pagine totali" : "Total pages",
    copied: isIt ? "Link copiato negli appunti" : "Link copied to clipboard",
    confirmRemoveTitle: isIt ? "Rimuovere dalla Hall?" : "Remove from the Hall?",
    confirmRemove: isIt
      ? "La pubblicazione e tutti i \"mi piace\" associati verranno eliminati. La tua avventura resta intatta."
      : "The publication and all its likes will be removed. Your adventure stays intact.",
    confirmRemoveYes: isIt ? "Sì, rimuovi" : "Yes, remove",
    confirmRemoveNo: isIt ? "Annulla" : "Cancel",
    aiBtn: isIt ? "Genera con IA" : "Generate with AI",
    aiBtnBusy: isIt ? "Sto componendo..." : "Composing...",
    aiHint: isIt
      ? "Lascia che l'IA scriva titolo e descrizione basandosi sulla tua trama. Puoi modificarli prima di pubblicare."
      : "Let the AI craft title and description based on your story. You can still edit them before publishing.",
    aiSuccess: isIt ? "Titolo e descrizione generati ✨" : "Title and description generated ✨",
    aiError: isIt ? "Generazione AI fallita, riprova" : "AI generation failed, try again",
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hall/by-game/${gameId}`, { headers });
      if (res.data?.published) {
        setStory(res.data.story);
        setTitle(res.data.story.title || "");
        setDescription(res.data.story.description || "");
      } else {
        setStory(null);
        // Sensible default title if user starts fresh
        setTitle(t(lang, "saved_adventures") || "");
        setDescription("");
      }
    } catch (e) {
      // If the GET fails we can still let the user try to publish.
      setStory(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gameId && token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, token]);

  const publishOrUpdate = async () => {
    setBusy(true);
    try {
      const trimmedTitle = (title || "").trim().slice(0, 80);
      const trimmedDesc = (description || "").trim().slice(0, 500);
      const res = await axios.post(
        `${API}/hall/publish`,
        { game_id: gameId, title: trimmedTitle, description: trimmedDesc },
        { headers }
      );
      if (res.data?.status === "updated") {
        toast.success(isIt ? "Pubblicazione aggiornata" : "Publication updated");
      } else {
        toast.success(isIt ? "Saga pubblicata nella Hall" : "Saga published to Hall");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    setConfirmRemoveOpen(false);
    setBusy(true);
    try {
      await axios.post(`${API}/hall/unpublish`, { game_id: gameId }, { headers });
      toast.success(isIt ? "Saga rimossa dalla Hall" : "Saga removed");
      setStory(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const generateWithAi = async () => {
    setAiBusy(true);
    try {
      const res = await axios.post(`${API}/hall/${gameId}/ai-summary`, {}, { headers });
      if (res.data?.title) setTitle(res.data.title);
      if (res.data?.description) setDescription(res.data.description);
      toast.success(STR.aiSuccess);
    } catch (e) {
      toast.error(e?.response?.data?.detail || STR.aiError);
    } finally {
      setAiBusy(false);
    }
  };

  const copyLink = async () => {
    if (!story?.id) return;
    const url = `${window.location.origin}/hall/${story.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(STR.copied);
    } catch {
      toast.info(url);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4 text-[#64748b] text-xs">
        <div className="w-3 h-3 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin mr-2" />
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="publish-story-panel">
      <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
        <Globe2 size={14} />
        {STR.section}
      </Label>
      <p className="text-xs text-[#64748b] leading-relaxed">{STR.intro}</p>

      {story && (
        <div className="bg-[#0a0a0c] border border-[#cfb53b]/40 rounded-md p-3 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="px-2 py-0.5 rounded-full bg-emerald-900/40 border border-emerald-700/40 text-emerald-300 font-heading">
              <Sparkles size={10} className="inline mr-1" />
              {STR.publishedBadge}
            </span>
            <button
              type="button"
              onClick={copyLink}
              className="text-[#cfb53b] hover:text-[#d4af37] flex items-center gap-1"
              data-testid="hall-copy-link"
            >
              <Share2 size={12} /> Copy
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px] text-[#94a3b8]">
            <div className="flex items-center gap-1">
              <Heart size={11} className="text-pink-400" />
              {STR.likes}: <strong className="text-[#e2e8f0]">{story.likes_count}</strong>
            </div>
            <div className="flex items-center gap-1">
              <Eye size={11} className="text-cyan-400" />
              {STR.views}: <strong className="text-[#e2e8f0]">{story.views}</strong>
            </div>
            <div>{STR.chapters}: <strong className="text-[#e2e8f0]">{story.chapters}</strong></div>
            <div>{STR.bosses}: <strong className="text-[#e2e8f0]">{story.bosses_defeated}</strong></div>
            <div className="col-span-2">{STR.pages}: <strong className="text-[#e2e8f0]">{story.total_pages}</strong></div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-[#94a3b8] text-xs">{STR.titleLabel}</Label>
          <button
            type="button"
            onClick={generateWithAi}
            disabled={aiBusy || busy}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-heading text-[#cfb53b] hover:text-[#e5cc5b] disabled:opacity-50 border border-[#cfb53b]/30 hover:border-[#cfb53b]/60 bg-[#cfb53b]/5 hover:bg-[#cfb53b]/10 px-2 py-1 rounded-sm transition-colors"
            data-testid="hall-ai-generate-btn"
            title={STR.aiHint}
          >
            {aiBusy ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
            {aiBusy ? STR.aiBtnBusy : STR.aiBtn}
          </button>
        </div>
        <Input
          data-testid="hall-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 80))}
          placeholder={STR.titlePlaceholder}
          className="bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0] text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-[#94a3b8] text-xs">{STR.descLabel}</Label>
        <Textarea
          data-testid="hall-desc-input"
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 500))}
          placeholder={STR.descPlaceholder}
          rows={3}
          className="bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0] text-sm resize-none"
        />
        <p className="text-[10px] text-[#64748b] text-right">{description.length}/500</p>
      </div>

      <div className="flex flex-col gap-2">
        <Button
          data-testid="hall-publish-btn"
          onClick={publishOrUpdate}
          disabled={busy}
          className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading w-full"
        >
          {busy ? "..." : story ? STR.updateBtn : STR.publishBtn}
        </Button>
        {story && (
          <Button
            data-testid="hall-unpublish-btn"
            onClick={() => setConfirmRemoveOpen(true)}
            disabled={busy}
            variant="outline"
            className="border-[#8b0000]/50 text-[#8b0000] hover:bg-[#8b0000]/10 hover:text-[#cfb53b] w-full"
          >
            <Trash2 size={14} className="mr-1.5" />
            {STR.unpublishBtn}
          </Button>
        )}
      </div>

      {/* Internal confirmation dialog (replaces the native window.confirm) */}
      <AlertDialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
        <AlertDialogContent
          className="bg-[#1a1a1d] border border-[#8b0000]/40 max-w-[90vw] sm:max-w-md"
          data-testid="hall-unpublish-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[#cfb53b] text-lg flex items-center gap-2">
              <Trash2 size={18} className="text-[#8b0000]" />
              {STR.confirmRemoveTitle}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#94a3b8] leading-relaxed">
              {STR.confirmRemove}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="hall-unpublish-cancel"
              className="bg-transparent border-[#2d3748] text-[#94a3b8] hover:bg-[#2d3748] hover:text-[#e2e8f0]"
            >
              {STR.confirmRemoveNo}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="hall-unpublish-confirm"
              onClick={unpublish}
              disabled={busy}
              className="bg-[#8b0000] hover:bg-[#a00000] text-white border-0"
            >
              <Trash2 size={14} className="mr-1.5" />
              {STR.confirmRemoveYes}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
